import React, { type FC, type FormEvent, useCallback, useState } from "react";
import notification from "@app/services/notification";
import FormFooter from "@shared/ui/form-footer";
import { Input } from "@shared/ui/input";
import { useWindowControls } from "@shared/ui/overlay/hooks/useWindowControls";
import { strIsEmpty } from "@src/helpers/text-services/stringEmpty";
import type { ScannerScanTarget } from "../types";
import "./ManualBarcodeEntryForm.scss";

/** Пропсы формы ручного ввода штрихкода. */
export interface ManualBarcodeEntryFormProps {
    /**
     * Цель inject, захваченная с active handler **до** открытия этого blocking-окна. \
     * Submit вызывает `scanTarget.inject`, а не live `activeEntry` (он сброшен `globalSuppress`).
     */
    scanTarget: ScannerScanTarget;
}

/**
 * Blocking-форма ручного ввода штрихкода для имитации аппаратного скана. \
 * Не регистрирует свой scanner-handler — inject идёт в заранее захваченный target.
 */
export const ManualBarcodeEntryForm: FC<ManualBarcodeEntryFormProps> = ({ scanTarget }) => {
    const { close } = useWindowControls();
    const [barcode, setBarcode] = useState("");
    const [showError, setShowError] = useState(false);

    const trimmedBarcode = barcode.trim();
    const isEmpty = strIsEmpty(trimmedBarcode);

    const applyBarcode = useCallback((): void => {
        if (isEmpty) {
            setShowError(true);
            return;
        }

        const accepted = scanTarget.inject(trimmedBarcode);
        if (!accepted) {
            notification.warning("Не удалось передать штрихкод: сценарий занят или уже закрыт. Закройте окно и повторите.");
            return;
        }

        close();
    }, [close, isEmpty, scanTarget, trimmedBarcode]);

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        applyBarcode();
    };

    return (
        <form className="manual_barcode_entry_form" onSubmit={handleSubmit}>
            <p className="manual_barcode_entry_form__hint">
                Введите штрихкод вручную — приложение обработает его как аппаратный скан текущего сценария.
            </p>
            <Input
                label="Штрихкод"
                value={barcode}
                required
                error={showError && isEmpty}
                description={showError && isEmpty ? "Введите непустой штрихкод" : undefined}
                spellCheck={false}
                autoComplete="off"
                onChange={(event) => {
                    setBarcode(event.target.value);
                    if (showError) {
                        setShowError(false);
                    }
                }}
            />
            <FormFooter confirmText="Применить" confirmDisabled={isEmpty} onConfirm={applyBarcode} />
        </form>
    );
};
